import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import zoomSdk from "@zoom/appssdk";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import summaryWithAI from "../../../../images/attendee_initial.svg";
import AttendeeSummarizingIndicator from "./AttendeeSummarizingIndicator";
import AttendeeSummarizeStopped from "./AttendeeSummarizeStopped";
import SummaryReady from "../organizer/SummaryReady";
import LogManager from "../../../../utils/logManager";
import { fetchRtmsSessionStatus, SESSION_STATUS } from "../../../../utils/rtmsSessionStatusUtils";
import { fetchSummaries } from "../../../../utils/summaryUtils";
import { ROLES, RTMS_REQUESTER_DEFAULT_NAME } from "../../../../constants";
import { THEME_COLOR } from "../../../../styles/colors";

/**
 * AttendeeHomeView Component: This component ensures attendees are kept informed about the summarization status
 *
 * This component is shown to attendees in a Zoom meeting when AiMS is enabled for the meeting.
 * It listens to RTMS status changes to inform attendees whether summarization is in progress/stopped.
 *
 * Key Features:
 * - Uses Zoom SDK's onRTMSStatusChange event listener to get real-time status updates.
 *
 *   The session status can be one of:
 *     - ACTIVE: Summarization is in progress → shows `AttendeeSummarizingIndicator`
 *     - INACTIVE: Summarization has ended → shows `AttendeeSummarizeStopped`
 *     - All other states → shows default static info view
 */
const AttendeeHomeView = ({ meetingUUID: initialMeetingUUID, aimsStatus: initialAimsStatus, role, initialSessionStatus, initialRtmsRequesterName, isAttendanceReportEnabled }) => {
  const navigate = useNavigate();
  const [meetingUUID, setMeetingUUID] = useState(initialMeetingUUID);
  const [rtmsSessionStatus, setRtmsSessionStatus] = useState(initialSessionStatus || null);
  const currentStatusRef = useRef(null);
  const [aimsStatus, setAimsStatus] = useState(initialAimsStatus);
  const [summaryReady, setSummaryReady] = useState(false);
  const [aimsUuid, setAimsUuid] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [rtmsStartTimestamp, setRtmsStartTimestamp] = useState(null);
  const rtmsStartTimestampRef = useRef(null);
  const summaryCheckTimeoutRef = useRef(null);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(initialRtmsRequesterName || "");
  const [summaryReviewers, setSummaryReviewers] = useState(null);

  // Function to check for summary after RTMS has ended
  const checkForSummary = async (queryStartTime, queryEndTime) => {
    LogManager.info(`[AttendeeHomeView] Checking for summary with time between ${queryStartTime} and ${queryEndTime}`);
    
    try {
      // Override the default time range in fetchSummaries with our specific time range
      const summaryListJson = await fetchSummaries(queryStartTime, queryEndTime, 1);
      
      if (summaryListJson && summaryListJson.summaries && summaryListJson.summaries.length > 0) {
        // Find a summary that matches our meeting UUID
        const summary = summaryListJson.summaries.find(s => s.meetingUuid === meetingUUID);
        if (summary) {
          LogManager.info("[AttendeeHomeView] Found matching summary:", summary.aimsUuid);
          setAimsUuid(summary.aimsUuid);
          setSummaryData(summary);
          setSummaryReady(true);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      LogManager.error("[AttendeeHomeView] Error checking for summary:", error);
      return false;
    }
  };

  /**
   * Handle RTMS status change events from Zoom SDK
   * @param {object} event - The RTMS status change event
   */
  async function handleRTMSChange(event) {
    console.log("RTMS changed:", JSON.stringify(event));
    LogManager.info(`[AttendeeHomeView] RTMS status changed to: ${event.status}`);
    
    if (event.status === "started") {
      setRtmsSessionStatus(SESSION_STATUS.ACTIVE);
      currentStatusRef.current = SESSION_STATUS.ACTIVE;
      // Record the timestamp when summarization starts
      const startTimestamp = Math.floor(Date.now() / 1000);
      setRtmsStartTimestamp(startTimestamp);
      rtmsStartTimestampRef.current = startTimestamp;
      LogManager.info("[AttendeeHomeView] RTMS started at timestamp:", startTimestamp);
      
      // Fetch session status to get who started the summary
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
        LogManager.info("Session status when started:", sessionStatus);
        
        // Check if the session was started by someone else
        if (!sessionStatus.rtmsRequesterAlias) {
          // No requester info available - this is problematic, set default name
          setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
        } else {
          // Started by someone else (always true for attendees)
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        }
      } catch (error) {
        LogManager.error("Error fetching session status on RTMS start:", error);
        // Fallback: set default name if we can't determine who started it
        setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
      }
    } else if (event.status && event.status === "stopped") {
      setRtmsSessionStatus(SESSION_STATUS.INACTIVE);
      currentStatusRef.current = SESSION_STATUS.INACTIVE;
      
      // Fetch session status to get reviewer data
      fetchRtmsSessionStatus(meetingUUID).then((sessionData) => {
        LogManager.info(`Session status: ${sessionData.status}, statusMessage: ${sessionData.statusMessage}`);
        
        // Extract reviewer data from session API response
        if (sessionData.reviewers) {
          LogManager.info("Summary reviewer data from session API:", sessionData.reviewers);
          setSummaryReviewers(sessionData.reviewers);
        }
      }).catch((error) => {
        LogManager.error("Error fetching session status on RTMS stop:", error);
      });
      
      // Clear any existing summary check timeout to prevent duplicates
      if (summaryCheckTimeoutRef.current) {
        clearTimeout(summaryCheckTimeoutRef.current);
      }
      
      // Start the summary checking process after RTMS stops
      summaryCheckTimeoutRef.current = setTimeout(async () => {
        if (rtmsStartTimestampRef.current === null) {
          LogManager.warn("[AttendeeHomeView] rtmsStartTimestamp is null, skipping summary check");
          return;
        }
        
        const queryStartTime = rtmsStartTimestampRef.current - 10; 
        const queryEndTime = rtmsStartTimestampRef.current + 60;
        
        LogManager.info(`[AttendeeHomeView] Using rtmsStartTimestamp: ${rtmsStartTimestampRef.current} for summary query`);
        await checkForSummary(queryStartTime, queryEndTime);
        summaryCheckTimeoutRef.current = null; // Clear the ref after execution
      }, 60000); // 1 minute
    }
  }

  useEffect(() => {

    if (!meetingUUID) return;

    // Set up RTMS status change listener
    try {
      LogManager.info(`[AttendeeHomeView] Setting up RTMS status change listener for meeting: ${meetingUUID}`);
      zoomSdk.onRTMSStatusChange(handleRTMSChange);
    } catch (error) {
      LogManager.error("[AttendeeHomeView] Error setting up RTMS status listener:", error);
      setRtmsSessionStatus(SESSION_STATUS.ERROR);
      currentStatusRef.current = SESSION_STATUS.ERROR;
    }

    // Cleanup function to clear timeouts when component unmounts
    return () => {
      if (summaryCheckTimeoutRef.current) {
        clearTimeout(summaryCheckTimeoutRef.current);
      }
    };
  }, [meetingUUID]);

  // Show summary ready component if summary is available
  if (summaryReady) {
    return (
      <SummaryReady 
        aimsUuid={aimsUuid} 
        onViewSummary={() => {
          navigate(`/meeting/summary/${aimsUuid}`, { state: { summary: summaryData, isAttendanceReportEnabled } });
        }} 
      />
    );
  }

  if (rtmsSessionStatus === SESSION_STATUS.ACTIVE) {
    return <AttendeeSummarizingIndicator rtmsRequesterName={rtmsRequesterName} />;
  }

  //TODO: For ERROR case, might needs to update in the future
  if (rtmsSessionStatus === SESSION_STATUS.INACTIVE || rtmsSessionStatus === SESSION_STATUS.ERROR) {
    return <AttendeeSummarizeStopped aimsStatus={aimsStatus} summaryReviewers={summaryReviewers} />;
  }

  return (
    <Column alignmentHorizontal="stretch" spacing="none">
      <Column height="72px" />
      <Box width="100%" position="relative">
        <img src={summaryWithAI} alt="summaryWithAI" style={{ display: "block", margin: "auto", maxWidth: "100%", height: "auto" }} />
      </Box>
      <Column height="16px" />
      <Column spacing="none">
        <Row spacing="none" spacingInset="none">
          <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
            <Box type="outline" spacingInset="none" minWidth="68px" />
          </Theme>
          <Text type="h50" alignment="start">
            Summarize meetings with AI
          </Text>
        </Row>
      </Column>
      <Column height="16px" spacing="none" />
      <Column spacing="none" spacingInset="none">
        <Row spacing="none" spacingInset="none">
          <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
            <Box type="outline" spacingInset="none" minWidth="68px" />
          </Theme>
          <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
            <Box type="outline" spacingInset="none" maxWidth="218px">
              <Theme tokens={{ textListSpacingVertical: "30px" }}>
                <Text type="b200" alignment="start" color="secondary">
                  {role === ROLES.ALTERNATIVE_HOST
                    ? "Only meeting organizer can turn on meeting summary. Alternative hosts can't turn it on for this meeting."
                    : "Meeting organizers can turn on meeting summary."
                  }
                </Text>
              </Theme>
            </Box>
          </Theme>
        </Row>
      </Column>
    </Column>
  );
};

export default AttendeeHomeView;
